import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  FormControl,
  InputLabel,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { API_BASE_URL } from '../services/api';

export default function MaturityBackOfficeVerifierBlotter() {
  const [deals, setDeals] = useState([]);
  const [selectedDeals, setSelectedDeals] = useState([]);
  const [processDate, setProcessDate] = useState(new Date().toISOString().slice(0, 10));
  const [maturityAction, setMaturityAction] = useState('');
  const [bankPaymentCode, setBankPaymentCode] = useState('');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [referenceDealInfo, setReferenceDealInfo] = useState(null);
  const [referenceDealLoading, setReferenceDealLoading] = useState(false);

  const MATURITY_ACTIONS = [
    { value: 'principal_interest_full_payment', label: '1. Principal and Interest Full Payment' },
    { value: 'principal_reinvest_interest_paid', label: '2. Principal Reinvest, Interest Paid' },
    { value: 'principal_interest_reinvest', label: '3. Principal and Interest Reinvest' },
    { value: 'different_amount_reinvest', label: '4. Different Amount Reinvest' }
  ];

  useEffect(() => {
    fetchDeals();
    fetchBankAccounts();
  }, [processDate]);

  const fetchDeals = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const response = await fetch(`${API_BASE_URL}/maturity/blotter?date=${processDate}`, {
        headers: {
          'x-user-data': JSON.stringify(user)
        }
      });
      const data = await response.json();
      if (data.success) {
        setDeals(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching deals:', error);
      setErrorMsg('Failed to fetch deals');
    } finally {
      setLoading(false);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/payment-master/modes`);
      const data = await response.json();
      if (data.success) {
        setBankAccounts(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
    }
  };

  const handleViewReferenceDeal = async (dealNumber) => {
    if (!dealNumber) return;
    setReferenceDealLoading(true);
    setReferenceDealInfo({ deal_number: dealNumber });
    try {
      const res = await fetch(`${API_BASE_URL}/isin-master/gsec/source-buy-deals?deal_numbers=${encodeURIComponent(dealNumber)}`);
      const data = await res.json();
      const found = (data.data || [])[0];
      setReferenceDealInfo(found || { deal_number: dealNumber, notFound: true });
    } catch (e) {
      setReferenceDealInfo({ deal_number: dealNumber, error: true });
    } finally {
      setReferenceDealLoading(false);
    }
  };

  const handleSelectDeal = (dealId) => {
    setSelectedDeals(prev => 
      prev.includes(dealId) 
        ? prev.filter(id => id !== dealId)
        : [...prev, dealId]
    );
  };

  const handleSelectAll = () => {
    if (selectedDeals.length === deals.length) {
      setSelectedDeals([]);
    } else {
      setSelectedDeals(deals.map(deal => deal.deal_id));
    }
  };

  const handleApprove = async () => {
    if (selectedDeals.length === 0) {
      setErrorMsg('Please select at least one deal');
      return;
    }
    if (!maturityAction) {
      setErrorMsg('Please select a maturity action');
      return;
    }

    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const payload = {
        dealIds: selectedDeals,
        processDate,
        maturityAction,
        bankPaymentCode: bankPaymentCode || undefined
      };

      const response = await fetch(`${API_BASE_URL}/maturity/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-data': JSON.stringify(user)
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        setSuccessMsg(`Successfully verified ${selectedDeals.length} deals`);
        setSelectedDeals([]);
        setMaturityAction('');
        setBankPaymentCode('');
        setApproveDialogOpen(false);
        fetchDeals();
      } else {
        setErrorMsg(data.error || 'Verification failed');
      }
    } catch (error) {
      console.error('Error verifying deals:', error);
      setErrorMsg('Failed to verify deals');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Card sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Maturity Back Office Verifier Blotter
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Process Date"
              type="date"
              value={processDate}
              onChange={(e) => setProcessDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button 
              variant="contained" 
              onClick={fetchDeals}
              disabled={loading}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedDeals.length === deals.length && deals.length > 0}
                    indeterminate={selectedDeals.length > 0 && selectedDeals.length < deals.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Deal ID</TableCell>
                <TableCell>Deal Number</TableCell>
                <TableCell>Product Type</TableCell>
                <TableCell>Counterparty</TableCell>
                <TableCell>Principal Amount</TableCell>
                <TableCell>Interest Amount</TableCell>
                <TableCell>Maturity Amount</TableCell>
                <TableCell>Maturity Action</TableCell>
                <TableCell>First Logged</TableCell>
                <TableCell>Current Stage</TableCell>
                <TableCell>Reference Deal</TableCell>
                <TableCell>Opening Balance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deals.map((deal, index) => (
                <TableRow key={`${deal.deal_id}-${deal.maturity_action}-${index}`}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedDeals.includes(deal.deal_id)}
                      onChange={() => handleSelectDeal(deal.deal_id)}
                    />
                  </TableCell>
                  <TableCell>{deal.deal_id}</TableCell>
                  <TableCell>{deal.deal_number}</TableCell>
                  <TableCell>
                    <Chip 
                      label={deal.product_type?.toUpperCase() || 'UNKNOWN'} 
                      color={
                        deal.product_type === 'money_market' ? 'primary' :
                        deal.product_type === 'gsec' ? 'secondary' :
                        deal.product_type === 'repo' ? 'success' : 'default'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{deal.counterparty_name || 'Unknown'}</TableCell>
                  <TableCell>{deal.principal_amount ? `LKR ${parseFloat(deal.principal_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}</TableCell>
                  <TableCell>{deal.interest_amount ? `LKR ${parseFloat(deal.interest_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {deal.maturity_amount ? `LKR ${parseFloat(deal.maturity_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>{deal.maturity_action}</TableCell>
                  <TableCell>{deal.first_logged_date}</TableCell>
                  <TableCell>
                    <Chip
                      label={
                        deal.current_stage === 1
                          ? 'Front Office Approved'
                          : deal.current_stage === 2
                          ? 'Back Office Verified'
                          : deal.current_stage === 3
                          ? 'Final Approved'
                          : 'Pending FO Approval'
                      }
                      color={
                        deal.current_stage === 1
                          ? 'primary'
                          : deal.current_stage === 2
                          ? 'secondary'
                          : deal.current_stage === 3
                          ? 'success'
                          : 'default'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {deal.reference_deal_number ? (
                      <Typography
                        component="span"
                        sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => handleViewReferenceDeal(deal.reference_deal_number)}
                      >
                        {deal.reference_deal_number}
                      </Typography>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {deal.opening_balance !== undefined && deal.opening_balance !== null
                      ? Number(deal.opening_balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      : '-'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {deals.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="textSecondary">
              No deals found for the selected date
            </Typography>
          </Box>
        )}

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={() => setApproveDialogOpen(true)}
            disabled={selectedDeals.length === 0}
          >
            Verify Selected ({selectedDeals.length})
          </Button>
        </Box>

        {/* Approval Dialog */}
        <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Verify Maturity Deals</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Maturity Action</InputLabel>
                  <Select
                    value={maturityAction}
                    label="Maturity Action"
                    onChange={(e) => setMaturityAction(e.target.value)}
                  >
                    {MATURITY_ACTIONS.map((action) => (
                      <MenuItem key={action.value} value={action.value}>
                        {action.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {(maturityAction === 'principal_interest_full_payment' || 
                maturityAction === 'principal_reinvest_interest_paid') && (
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Settlement Mode</InputLabel>
                    <Select
                      value={bankPaymentCode}
                      label="Settlement Mode"
                      onChange={(e) => setBankPaymentCode(e.target.value)}
                    >
                      {bankAccounts.map((mode) => (
                        <MenuItem key={`${mode.payment_method}-${mode.bank_payment_code}`} value={mode.bank_payment_code}>
                          {mode.payment_method} - {mode.bank_payment_code}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleApprove} variant="contained" disabled={loading}>
              Verify
            </Button>
          </DialogActions>
        </Dialog>

        {successMsg && (
          <Snackbar open autoHideDuration={3000} onClose={() => setSuccessMsg('')}>
            <Alert severity="success">{successMsg}</Alert>
          </Snackbar>
        )}
        {errorMsg && (
          <Snackbar open autoHideDuration={3000} onClose={() => setErrorMsg('')}>
            <Alert severity="error">{errorMsg}</Alert>
          </Snackbar>
        )}

        <Dialog open={Boolean(referenceDealInfo)} onClose={() => setReferenceDealInfo(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Reference Deal - {referenceDealInfo?.deal_number}</DialogTitle>
          <DialogContent>
            {referenceDealLoading && <Typography>Loading...</Typography>}
            {!referenceDealLoading && referenceDealInfo?.notFound && (
              <Typography color="text.secondary">Deal not found.</Typography>
            )}
            {!referenceDealLoading && referenceDealInfo?.error && (
              <Typography color="error">Failed to load deal details.</Typography>
            )}
            {!referenceDealLoading && referenceDealInfo && !referenceDealInfo.notFound && !referenceDealInfo.error && (
              <Box>
                <Typography>ISIN: {referenceDealInfo.isin}</Typography>
                <Typography>Portfolio: {referenceDealInfo.portfolio}</Typography>
                <Typography>Value Date: {referenceDealInfo.value_date ? String(referenceDealInfo.value_date).split('T')[0] : ''}</Typography>
                <Typography>Face Value: {referenceDealInfo.face_value ? Number(referenceDealInfo.face_value).toLocaleString() : ''}</Typography>
                <Typography>Remaining Face Value: {referenceDealInfo.remaining_face_value ? Number(referenceDealInfo.remaining_face_value).toLocaleString() : ''}</Typography>
                <Typography>Yield: {referenceDealInfo.yield}</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReferenceDealInfo(null)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Card>
    </Container>
  );
}
